/**
 * The performer's phone panel shell (LIAA service plan S3.2 — P2-2; design MobMyDay / MobJob / MobCapture): a
 * mobile-first layout next to AppLayout, like the gate panel's — no sider, one compact header (up / title / user),
 * big touch targets below. "Up" goes to the panel's home, never through history. A page that must not be left
 * mid-way (an upload running) registers a leave guard, which the up button asks first.
 */

import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Layout, Spin, theme, Typography } from 'antd';
import { Suspense, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { UserPill } from './UserPill';

import type { LeaveGuard } from '@/features/work-orders';

import { LeaveGuardContext } from '@/features/work-orders';
import { RouteErrorBoundary } from '@/shared/ui/AppErrorBoundary';

const { Header, Content } = Layout;

export function MyWorkLayout(): React.JSX.Element {
  const { t } = useTranslation('workOrders');
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const guard = useRef<LeaveGuard | null>(null);
  const setGuard = useCallback((next: LeaveGuard | null) => {
    guard.current = next;
  }, []);
  const atHome = location.pathname === '/my-work';
  // The capture screen's parent is its job; everything else goes up to the list.
  const capture = /^\/my-work\/([^/]+)\/capture$/.exec(location.pathname);
  const captureLine = new URLSearchParams(location.search).get('line');
  const upTarget = capture !== null && captureLine !== null ? `/my-work/${capture[1] ?? ''}/${captureLine}` : '/my-work';

  async function goUp(): Promise<void> {
    if (guard.current !== null && !(await guard.current())) return;
    navigate(upTarget);
  }

  return (
    <LeaveGuardContext.Provider value={setGuard}>
      <Layout style={{ minHeight: '100vh' }}>
        <Header style={{ display: 'flex', alignItems: 'center', gap: 8, paddingInline: 12, background: token.colorBgElevated }}>
          {!atHome && <Button type="text" size="large" icon={<ArrowLeftOutlined />} aria-label={t('panel.back')} onClick={() => void goUp()} />}
          <Typography.Text strong style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {t('panel.title')}
          </Typography.Text>
          <UserPill />
        </Header>
        <Content style={{ padding: 12, maxWidth: 640, width: '100%', marginInline: 'auto' }}>
          <RouteErrorBoundary>
            <Suspense fallback={<Spin />}>
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </Content>
      </Layout>
    </LeaveGuardContext.Provider>
  );
}
